import React, { useState } from "react";
import type { CartItem } from "../types/pos";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import UndoIcon from "@mui/icons-material/Undo";
import DiscountIcon from "@mui/icons-material/LocalOffer";
import Tooltip from "@mui/material/Tooltip";
import TextField from "@mui/material/TextField";

type Props = {
  cart: CartItem[];
  setCart: (cart: CartItem[]) => void;
};

type EditState = { idx: number; field: "name" | "price" | "discount"; value: string | number } | null;

const CartPanel: React.FC<Props> = ({ cart, setCart }) => {
  const [edit, setEdit] = useState<EditState>(null);
  const [lastRemoved, setLastRemoved] = useState<CartItem | null>(null);

  // Edit handler
  const handleEdit = (idx: number, field: "name" | "price" | "discount", value: string | number) => {
    setCart(
      cart.map((item, i) =>
        i === idx
          ? {
              ...item,
              [field]: field === "name" ? value : Number(value),
              lineTotal:
                (field === "price"
                  ? Number(value)
                  : item.sellingPrice ?? item.price) *
                  item.quantity -
                (field === "discount" ? Number(value) : item.discount ?? 0),
            }
          : item
      )
    );
    setEdit(null);
  };

  // Quantity change
  const handleQtyChange = (idx: number, qty: number) => {
    setCart(
      cart.map((item, i) =>
        i === idx
          ? {
              ...item,
              quantity: qty,
              lineTotal: (item.sellingPrice ?? item.price) * qty - (item.discount ?? 0),
            }
          : item
      )
    );
  };

  // Remove with undo
  const handleRemove = (idx: number) => {
    setLastRemoved(cart[idx]);
    setCart(cart.filter((_, i) => i !== idx));
  };
  const handleUndoRemove = () => {
    if (lastRemoved) setCart([lastRemoved, ...cart]);
    setLastRemoved(null);
  };

  // Discount inline edit
  const handleDiscountEdit = (idx: number) => {
    setEdit({ idx, field: "discount", value: cart[idx].discount ?? 0 });
  };

  // Subtotal, total discount, tax, grand total
  const subtotal = cart.reduce((sum, item) => sum + (item.sellingPrice ?? item.price) * item.quantity, 0);
  const totalDiscount = cart.reduce((sum, item) => sum + (item.discount ?? 0), 0);
  const totalTax = cart.reduce(
    (sum, item) =>
      sum +
      ((item.sellingPrice ?? item.price) * item.quantity -
        (item.discount ?? 0)) *
        ((item.taxRate ?? 0) / 100),
    0
  );
  const grandTotal = subtotal - totalDiscount + totalTax;

  return (
    <div>
      <h2 className="text-lg font-semibold mb-2">Order Cart</h2>
      <div className="overflow-x-auto">
        <table className="w-full text-sm border">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-2 py-1">Item</th>
              <th>Qty</th>
              <th>Each</th>
              <th>Discount</th>
              <th>Tax</th>
              <th>Total</th>
              <th className="text-center" />
            </tr>
          </thead>
          <tbody>
            {cart.map((item, idx) => (
              <tr
                key={item._id}
                className={idx % 2 === 1 ? "bg-gray-50" : ""}
              >
                {/* Name editable */}
                <td>
                  {edit && edit.idx === idx && edit.field === "name" ? (
                    <TextField
                      size="small"
                      value={edit.value}
                      onChange={e => setEdit({ ...edit, value: e.target.value })}
                      onBlur={() => handleEdit(idx, "name", edit.value)}
                      autoFocus
                    />
                  ) : (
                    <span className="flex items-center gap-1">
                      {item.name}
                      <Tooltip title="Edit Name">
                        <button
                          onClick={() => setEdit({ idx, field: "name", value: item.name })}
                          className="ml-1 text-gray-400 hover:text-blue-700"
                        >
                          <EditIcon fontSize="small" />
                        </button>
                      </Tooltip>
                    </span>
                  )}
                </td>
                {/* Qty */}
                <td>
                  <TextField
                    size="small"
                    type="number"
                    inputProps={{ min: 1 }}
                    value={item.quantity}
                    onChange={(e) => handleQtyChange(idx, Math.max(1, Number(e.target.value)))}
                    sx={{ width: 72 }}
                  />
                </td>
                {/* Price editable */}
                <td>
                  {edit && edit.idx === idx && edit.field === "price" ? (
                    <TextField
                      size="small"
                      value={edit.value}
                      onChange={e => setEdit({ ...edit, value: e.target.value })}
                      onBlur={() => handleEdit(idx, "price", edit.value)}
                      autoFocus
                      type="number"
                      inputProps={{ min: 0, step: 0.01 }}
                    />
                  ) : (
                    <span className="flex items-center gap-1">
                      ${(item.sellingPrice ?? item.price).toFixed(2)}
                      <Tooltip title="Edit Price">
                        <button
                          onClick={() =>
                            setEdit({ idx, field: "price", value: item.sellingPrice ?? item.price })
                          }
                          className="ml-1 text-gray-400 hover:text-blue-700"
                        >
                          <EditIcon fontSize="small" />
                        </button>
                      </Tooltip>
                    </span>
                  )}
                </td>
                {/* Discount editable */}
                <td>
                  {edit && edit.idx === idx && edit.field === "discount" ? (
                    <TextField
                      size="small"
                      value={edit.value}
                      onChange={e => setEdit({ ...edit, value: e.target.value })}
                      onBlur={() => handleEdit(idx, "discount", edit.value)}
                      autoFocus
                      type="number"
                      inputProps={{ min: 0, step: 0.01 }}
                    />
                  ) : (
                    <span className="flex items-center gap-1">
                      ${item.discount?.toFixed(2) ?? "0.00"}
                      <Tooltip title="Edit Discount">
                        <button
                          onClick={() => handleDiscountEdit(idx)}
                          className="ml-1 text-gray-400 hover:text-green-700"
                        >
                          <DiscountIcon fontSize="small" />
                        </button>
                      </Tooltip>
                    </span>
                  )}
                </td>
                {/* Tax */}
                <td>
                  {item.taxRate ? `${item.taxRate}%` : "—"}
                </td>
                {/* Line total */}
                <td>
                  $
                  {(
                    ((item.sellingPrice ?? item.price) * item.quantity -
                      (item.discount ?? 0)) *
                    (1 + (item.taxRate ?? 0) / 100)
                  ).toFixed(2)}
                </td>
                {/* Actions */}
                <td className="text-center">
                  <Tooltip title="Remove">
                    <button
                      onClick={() => handleRemove(idx)}
                      className="text-red-500 font-bold"
                    >
                      <DeleteIcon fontSize="small" />
                    </button>
                  </Tooltip>
                </td>
              </tr>
            ))}
            {/* Undo row */}
            {lastRemoved && (
              <tr>
                <td colSpan={7} className="text-center py-2 bg-yellow-50">
                  <span>Item removed.</span>
                  <button
                    className="ml-4 text-blue-600 hover:underline inline-flex items-center"
                    onClick={handleUndoRemove}
                  >
                    <UndoIcon fontSize="small" className="mr-1" />
                    Undo
                  </button>
                </td>
              </tr>
            )}
            {/* Summary row */}
            <tr className="bg-blue-50 font-bold sticky bottom-0">
              <td colSpan={3} className="text-right">Subtotal:</td>
              <td colSpan={1}>${subtotal.toFixed(2)}</td>
              <td className="text-right">Discounts:</td>
              <td colSpan={2}>-${totalDiscount.toFixed(2)}</td>
            </tr>
            <tr className="bg-blue-100 font-bold">
              <td colSpan={5} className="text-right">Tax:</td>
              <td colSpan={2}>${totalTax.toFixed(2)}</td>
            </tr>
            <tr className="bg-blue-200 font-bold text-lg">
              <td colSpan={5} className="text-right">Grand Total:</td>
              <td colSpan={2}>${grandTotal.toFixed(2)}</td>
            </tr>
          </tbody>
        </table>
      </div>
      {cart.length === 0 && (
        <div className="text-gray-400 text-center py-4">No items yet</div>
      )}
    </div>
  );
};

export default CartPanel;
